/**
 * DeliveryModeToggle - Premium segmented control für Lieferung/Abholung
 * 
 * iOS-style pill slider mit smooth transitions.
 * Lieferando-inspiriert aber hochwertiger.
 */
import { memo, useCallback } from 'react';
import { Bike, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFilterStore, type DeliveryMode } from '@/lib/FilterStore';

interface DeliveryModeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  /** Override internal store - useful for controlled usage */
  value?: DeliveryMode;
  onChange?: (mode: DeliveryMode) => void;
}

const modes = [
  { 
    value: 'delivery' as const, 
    label: 'Lieferung', 
    icon: Bike,
    ariaLabel: 'Lieferung auswählen'
  },
  { 
    value: 'pickup' as const, 
    label: 'Abholung', 
    icon: Store,
    ariaLabel: 'Abholung auswählen'
  }
] as const;

export const DeliveryModeToggle = memo(function DeliveryModeToggle({
  className,
  size = 'md',
  value: controlledValue,
  onChange: controlledOnChange
}: DeliveryModeToggleProps) {
  // Store integration - fallback wenn nicht kontrolliert
  const storeMode = useFilterStore((s) => s.deliveryMode);
  const setStoreMode = useFilterStore((s) => s.setDeliveryMode);
  
  // Controlled vs uncontrolled
  const currentMode = controlledValue ?? storeMode;
  const handleChange = useCallback((mode: DeliveryMode) => {
    if (controlledOnChange) {
      controlledOnChange(mode);
    } else if (setStoreMode) {
      setStoreMode(mode);
    }
  }, [controlledOnChange, setStoreMode]);
  
  // Active index for CSS-based pill positioning (0 = delivery, 1 = pickup)
  const activeIndex = currentMode === 'delivery' ? 0 : 1;
  
  // Size variants
  const sizeClasses = {
    sm: {
      container: 'min-w-[160px] p-0.5 rounded-lg',
      button: 'px-3 py-1.5 text-xs gap-1.5 rounded-md',
      icon: 'w-3.5 h-3.5',
      pill: 'rounded-md'
    },
    md: {
      container: 'min-w-[200px] md:min-w-[220px] p-1 rounded-xl',
      button: 'px-4 py-1.5 text-sm gap-2 rounded-lg',
      icon: 'w-4 h-4',
      pill: 'rounded-lg'
    },
    lg: {
      container: 'min-w-[240px] md:min-w-[280px] p-1.5 rounded-2xl',
      button: 'px-5 py-2.5 text-base gap-2 rounded-xl',
      icon: 'w-5 h-5',
      pill: 'rounded-xl'
    }
  };
  
  const sizes = sizeClasses[size];
  
  return (
    <div
      className={cn(
        // Base container
        "relative inline-flex bg-gray-100",
        // Shadow & border for premium feel
        "shadow-sm border border-gray-200/50",
        // Size variant
        sizes.container,
        className
      )}
      role="tablist"
      aria-label="Lieferart auswählen"
    >
      {/* Animated pill background - CSS-only positioning */}
      <div
        className={cn(
          "absolute bg-[#009544]",
          "transition-colors duration-300 ease-out",
          "shadow-md",
          // Width: 50% minus padding
          "w-[calc(50%-2px)]",
          sizes.pill
        )}
        style={{
          // Positioning via CSS calc: 0 or 50%
          left: activeIndex === 0 ? '2px' : 'calc(50% + 0px)',
          top: size === 'sm' ? '2px' : '4px',
          bottom: size === 'sm' ? '2px' : '4px'
        }}
        aria-hidden="true"
      />
      
      {/* Toggle buttons */}
      {modes.map((mode) => {
        const Icon = mode.icon;
        const isActive = currentMode === mode.value;
        
        return (
          <button
            key={mode.value}
            onClick={() => handleChange(mode.value)}
            className={cn(
              // Layout
              "relative z-10 flex items-center justify-center flex-1",
              "font-medium select-none",
              // Transitions
              "transition-colors duration-200",
              // Size variant
              sizes.button,
              // Color states
              isActive 
                ? "text-white" 
                : "text-gray-600 hover:text-gray-800"
            )}
            role="tab"
            aria-selected={isActive}
            aria-label={mode.ariaLabel}
            tabIndex={isActive ? 0 : -1}
          >
            <Icon 
              className={cn(
                sizes.icon,
                "transition-transform duration-200",
                isActive && "scale-105"
              )} 
              strokeWidth={isActive ? 2.5 : 2}
            />
            <span>{mode.label}</span>
          </button>
        );
      })}
    </div>
  );
});

/**
 * Compact version for mobile header
 */
export const DeliveryModeToggleCompact = memo(function DeliveryModeToggleCompact(
  props: Omit<DeliveryModeToggleProps, 'size'>
) {
  return <DeliveryModeToggle {...props} size="sm" />;
});

export default DeliveryModeToggle;
